import { Box, useTheme } from "@mui/material";
import type { BoxProps } from "@mui/material";
import { API_URL } from "@/shared/config";
import { useWebTheme } from "../../hooks/useWebTheme";

interface HaveAGoodayLogoProps extends Omit<BoxProps, "children"> {
  size?: number;
  showText?: boolean;
  variant?: "default" | "minimal" | "full";
  tone?: "default" | "black";
}

/** Build full logo URL: API returns relative paths (/media/...), prepend API base URL */
function getLogoSrc(logoUrl: string | null | undefined): string {
  if (!logoUrl) return "/logo_dark.png";
  if (logoUrl.startsWith("http://") || logoUrl.startsWith("https://")) return logoUrl;
  const base = API_URL.replace(/\/$/, "");
  return `${base}${logoUrl.startsWith("/") ? "" : "/"}${logoUrl}`;
}

export default function HaveAGoodayLogo({
  size = 48,
  showText = true,
  variant = "default",
  tone = "default",
  sx,
  ...props
}: HaveAGoodayLogoProps) {
  const { mode, logoUrl, displayTitle } = useWebTheme();
  const theme = useTheme();
  const logoSize = size;
  const textSize = size * 0.6;

  const logoSrc = getLogoSrc(logoUrl);
  const displayName = displayTitle ?? "Haveagooday";

  const isBlack = tone === "black";
  const blackText = "#1A1A1A";

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        ...sx,
      }}
      {...props}
    >
      <Box
        sx={{
          width: logoSize,
          height: logoSize,
          borderRadius: "50%",
          background: theme.palette.background.default,
          border: `1px solid ${theme.palette.divider}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow:
            mode === "dark"
              ? "0 2px 8px color-mix(in srgb, var(--color-primary, #FFD700) 30%, transparent)"
              : "0 2px 8px rgba(0, 0, 0, 0.1)",
          overflow: "hidden",
          transition: "all 0.3s ease-in-out",
          "&:hover": {
            transform: "scale(1.05)",
            boxShadow:
              mode === "dark"
                ? "0 4px 12px color-mix(in srgb, var(--color-primary, #FFD700) 40%, transparent)"
                : "0 4px 12px rgba(0, 0, 0, 0.15)",
          },
        }}
      >
        <img
          src={logoSrc}
          alt="HaveAGooday Logo"
          style={{
            width: "100%",
            height: "100%",
            objectFit: "contain",
            display: "block",
          }}
        />
      </Box>

      {showText && variant !== "minimal" && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            pb: 0.15,
          }}
        >
          <Box
            component="span"
            sx={{
              fontSize: textSize,
              fontWeight: "bold",
              ...(isBlack
                ? {
                    color: blackText,
                    lineHeight: 1.08,
                    pb: "0.06em",
                  }
                : {
                    background:
                      (mode === "personal" || (mode as any) === "system")
                        ? "linear-gradient(135deg, var(--color-secondary), var(--color-success))"
                        : "linear-gradient(135deg, var(--color-primary), var(--color-secondary))",
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    lineHeight: 1.08,
                    pb: "0.06em",
                  }),
            }}
          >
            {displayName}
          </Box>
          {variant === "full" && (
            <Box
              component="span"
              sx={{
                fontSize: textSize * 0.4,
                color: isBlack ? blackText : "text.secondary",
                fontWeight: 400,
                lineHeight: 1,
                mt: 0.2,
              }}
            >
              Service Platform
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}
